/**
 * SourcesSideSheet — slide-in panel showing the corpus citations that
 * grounded an AI answer. Triggered from the "N مصدر قانوني مُستخدم"
 * badge below an answer bubble. Pure black/white styling per Harvey
 * design system.
 *
 * Citation shape (from envelope `result.sources`):
 *   {
 *     law_name:         'companies_law' | 'نظام الشركات' | …
 *     law_name_arabic:  'نظام الشركات'  (added by orchestrator)
 *     article_label:    'المادة الثانية بعد المائتين' | null
 *     snippet:          '…',
 *     similarity:       0.0-1.0,
 *     url:              'https://laws.boe.gov.sa/…' | null,
 *   }
 */
import React, { useEffect } from 'react';

const escClose = (onClose) => (e) => {
  if (e.key === 'Escape') onClose?.();
};

export function SourcesSideSheet({ isOpen, onClose, citations = [], isArabic = true, highlightedCitation = null }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = escClose(onClose);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // When opened via an inline `[n]` click (not the badge), scroll to
  // the matching card and flash its border so the user can spot it
  // quickly. We wait one tick after `isOpen` flips so the panel is
  // mounted in the DOM before we call `scrollIntoView`.
  useEffect(() => {
    if (!isOpen || highlightedCitation === null || highlightedCitation === undefined) return undefined;
    const id = window.requestAnimationFrame(() => {
      const card = document.getElementById(`citation-card-${highlightedCitation}`);
      if (!card) return;
      try {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {
        card.scrollIntoView();
      }
      const prevTransition = card.style.transition;
      const prevBorder = card.style.borderColor;
      card.style.transition = 'border-color 0.15s ease';
      card.style.borderColor = '#111111';
      window.setTimeout(() => {
        card.style.transition = 'border-color 0.5s ease';
        card.style.borderColor = prevBorder || '#E8E8E8';
        window.setTimeout(() => {
          card.style.transition = prevTransition;
        }, 600);
      }, 800);
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, highlightedCitation]);

  if (!isOpen) return null;

  const list = Array.isArray(citations) ? citations : [];

  return (
    <>
      {/* Click-outside dimmer (transparent — keeps the chat readable) */}
      <button
        type="button"
        aria-label={isArabic ? 'إغلاق المصادر' : 'Close sources'}
        onClick={onClose}
        data-testid="sources-side-sheet-overlay"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'transparent',
          border: 0,
          padding: 0,
          margin: 0,
          zIndex: 49,
          cursor: 'default',
        }}
      />
      <aside
        role="dialog"
        aria-modal="false"
        aria-label={isArabic ? 'المصادر القانونية المستخدمة' : 'Sources used'}
        dir={isArabic ? 'rtl' : 'ltr'}
        className="font-arabic"
        data-testid="sources-side-sheet"
        style={{
          position: 'fixed',
          [isArabic ? 'left' : 'right']: 0,
          top: '48px',
          width: '360px',
          height: 'calc(100vh - 48px)',
          background: '#FFFFFF',
          borderInlineStart: isArabic ? '1px solid #E8E8E8' : 'none',
          borderInlineEnd: isArabic ? 'none' : '1px solid #E8E8E8',
          zIndex: 50,
          overflowY: 'auto',
          padding: '24px 20px',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 500, color: '#111111', margin: 0 }}>
              {isArabic ? 'المصادر القانونية المستخدمة' : 'Sources used'}
            </h3>
            <p style={{ fontSize: '12px', color: '#999999', marginTop: '4px', marginBottom: 0, lineHeight: 1.5 }}>
              {isArabic
                ? 'هذه الإجابة مبنية على المقاطع التالية من الأنظمة السعودية المفهرسة'
                : 'This answer is grounded in the following passages from the indexed Saudi corpus'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={isArabic ? 'إغلاق' : 'Close'}
            data-testid="sources-side-sheet-close"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: '18px', color: '#999999', padding: '0 4px', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {list.length === 0 ? (
          <div style={{ fontSize: '12px', color: '#999999' }}>
            {isArabic ? 'لا توجد مصادر مرفقة بهذه الإجابة.' : 'No sources attached to this answer.'}
          </div>
        ) : (
          list.map((c, i) => {
            const law = c.law_name_arabic || c.law_name || (isArabic ? 'مصدر' : 'Source');
            const article = c.article_label || c.article || null;
            const snippet = (c.snippet || c.content || '').toString();
            const sim = typeof c.similarity === 'number' ? c.similarity : Number(c.similarity || 0);
            const isWeb = (c.source || '') === 'web';
            const url = c.url || null;
            return (
              <div
                key={`${law}-${article || ''}-${i}`}
                id={`citation-card-${i}`}
                data-testid={`sources-side-sheet-card-${i}`}
                data-law-name={c.law_name || ''}
                data-law-name-arabic={c.law_name_arabic || ''}
                data-source={c.source || ''}
                data-similarity={typeof c.similarity === 'number' ? c.similarity.toFixed(4) : ''}
                data-law-weight={typeof c.law_weight_applied === 'number' ? c.law_weight_applied.toFixed(2) : ''}
                style={{
                  background: '#F7F7F5',
                  border: '1px solid #E8E8E8',
                  borderRadius: '8px',
                  padding: '14px',
                  marginBottom: '10px',
                }}
              >
                <div style={{
                  fontSize: '11px', fontWeight: 500, color: '#111111',
                  marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <span aria-hidden="true" style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    background: '#111111', display: 'inline-block', flexShrink: 0,
                  }} />
                  {law}
                  {isWeb && (
                    <span style={{
                      fontSize: '9px', color: '#666666', background: '#EFEFEC',
                      padding: '1px 6px', borderRadius: '9999px', marginInlineStart: '4px',
                    }}>
                      {isArabic ? 'ويب' : 'web'}
                    </span>
                  )}
                </div>

                {article && (
                  <div style={{ fontSize: '11px', color: '#666666', marginBottom: '8px' }}>
                    {article}
                  </div>
                )}

                <div style={{
                  fontSize: '12px', color: '#444444', lineHeight: 1.7,
                  direction: 'rtl', textAlign: 'right',
                }}>
                  &ldquo;{snippet.length > 240 ? `${snippet.slice(0, 240)}…` : snippet}&rdquo;
                </div>

                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginTop: '10px',
                }}>
                  <span style={{ fontSize: '10px', color: '#BBBBBB' }}>
                    {isArabic ? 'تطابق' : 'match'}: {Math.round((sim || 0) * 100)}%
                  </span>
                  {url && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '10px', color: '#666666', textDecoration: 'underline' }}
                      data-testid={`sources-side-sheet-link-${i}`}
                    >
                      {isArabic ? 'فتح المصدر' : 'open source'}
                    </a>
                  )}
                </div>
              </div>
            );
          })
        )}
      </aside>
    </>
  );
}

export default SourcesSideSheet;
